import { Column, Pie } from '@ant-design/plots';
import { ProCard } from '@ant-design/pro-components';
import { useRequest } from 'ahooks';
import { Col, Empty, message, Row, Space, Statistic, Table } from 'antd';
import { useMemo, useState } from 'react';
import { examReportController } from '../../api';
import DepartmentSelect from '../../components/DepartmentSelect';
import { useCompany } from '../../hooks/company';
import { formatDuration } from './constants';

// 考试报表（考试成绩页的 Tab）：本部门及子部门的统计分析（权限 page:exam:report:department）
export default ({ examId }: { examId: I.ID }) => {
  const { id: companyId } = useCompany();
  const [departmentId, setDepartmentId] = useState<I.ID>();

  const { data, loading } = useRequest(
    () => examReportController.departmentReport(companyId, examId, departmentId),
    {
      refreshDeps: [examId, departmentId],
      onError: (e) => message.error(e.message),
    },
  );

  const summary = data?.summary;

  const passPieData = useMemo(() => {
    if (!summary?.attendedCount) {
      return [];
    }
    return [
      { type: '通过', value: summary.passedCount || 0 },
      {
        type: '未通过',
        value: (summary.attendedCount || 0) - (summary.passedCount || 0),
      },
    ];
  }, [summary]);

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <DepartmentSelect
          style={{ minWidth: 220 }}
          placeholder="部门（默认本部门及子部门）"
          value={departmentId}
          onChange={(value) => setDepartmentId(value)}
        />
      </Space>
      <Row gutter={16}>
        <Col span={4}>
          <ProCard>
            <Statistic title="应考人数" value={summary?.requiredCount ?? 0} />
          </ProCard>
        </Col>
        <Col span={4}>
          <ProCard>
            <Statistic
              title="参考人数"
              value={summary?.attendedCount ?? 0}
              suffix={`/ ${summary?.attendRate ?? 0}%`}
            />
          </ProCard>
        </Col>
        <Col span={4}>
          <ProCard>
            <Statistic
              title="通过人数"
              value={summary?.passedCount ?? 0}
              suffix={`/ ${summary?.passRate ?? 0}%`}
            />
          </ProCard>
        </Col>
        <Col span={4}>
          <ProCard>
            <Statistic title="平均分" value={summary?.avgScore ?? 0} />
          </ProCard>
        </Col>
        <Col span={4}>
          <ProCard>
            <Statistic
              title="最高 / 最低分"
              value={`${summary?.maxScore ?? 0} / ${summary?.minScore ?? 0}`}
            />
          </ProCard>
        </Col>
        <Col span={4}>
          <ProCard>
            <Statistic
              title="平均用时"
              value={formatDuration(Math.round(summary?.avgDurationSeconds || 0))}
            />
          </ProCard>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={8}>
          <ProCard title="通过占比">
            {passPieData.length ? (
              <Pie
                height={260}
                loading={loading}
                data={passPieData}
                angleField="value"
                colorField="type"
                radius={0.8}
                label={{ type: 'outer' }}
              />
            ) : (
              <Empty />
            )}
          </ProCard>
        </Col>
        <Col span={16}>
          <ProCard title="分数段分布（按得分率）">
            <Column
              height={260}
              loading={loading}
              data={data?.scoreDistribution || []}
              xField="bucket"
              yField="count"
              tooltip={{
                formatter: (item: any) => ({
                  name: '人数',
                  value: item.count,
                }),
              }}
            />
          </ProCard>
        </Col>
      </Row>

      <ProCard title="子部门统计" style={{ marginTop: 16 }}>
        <Column
          height={300}
          loading={loading}
          data={(data?.byDepartment || []).map((item) => ({
            department: item.departmentName,
            passRate: item.passRate || 0,
          }))}
          xField="department"
          yField="passRate"
          tooltip={{
            formatter: (item: any) => ({
              name: '通过率',
              value: `${item.passRate}%`,
            }),
          }}
        />
        <Table
          style={{ marginTop: 16 }}
          rowKey="departmentId"
          size="small"
          pagination={false}
          loading={loading}
          dataSource={data?.byDepartment}
          columns={[
            { title: '部门', dataIndex: 'departmentName' },
            { title: '应考', dataIndex: 'requiredCount', width: 90 },
            { title: '参考', dataIndex: 'attendedCount', width: 90 },
            { title: '通过', dataIndex: 'passedCount', width: 90 },
            {
              title: '通过率',
              dataIndex: 'passRate',
              width: 100,
              render: (value?: number) => `${value ?? 0}%`,
            },
            { title: '平均分', dataIndex: 'avgScore', width: 100 },
            {
              title: '平均用时',
              dataIndex: 'avgDurationSeconds',
              width: 120,
              render: (value?: number) => formatDuration(Math.round(value || 0)),
            },
          ]}
        />
      </ProCard>
    </>
  );
};
